import { Location } from "@angular/common";
import { Injectable } from "@angular/core";
import { Router, NavigationEnd, NavigationExtras } from "@angular/router";
import { CartAttributes } from "@models/cart.model";
import { Subject } from "rxjs";
import { filter, startWith } from "rxjs/operators";

@Injectable({
    providedIn: "root",
})
export class RouteTrackingService {
    private _previousUrl: string = "";
    private _currentUrl: string = "";
    private _intendedUrl: string = "";
    private _previousProduct: CartAttributes[] | undefined;

    public urlChanged: Subject<{ previousUrl: string; currentUrl: string; intendedUrl: string }> = new Subject();

    constructor(private router: Router) {
        this._currentUrl = this.router.url;

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            // Update route tracking
            this._previousUrl = this._currentUrl;
            this._currentUrl = event.url;
            const product = history.state.productData;
            if (product !== undefined) {
                this._previousProduct = product;
            }
            if (
                (this._previousUrl === "/cart" && this._currentUrl === "/checkout") ||
                (this._previousUrl === "/home" && this._currentUrl === "/checkout")
            ) {
                this._previousProduct = undefined;
            }
            // Clear intended destination when navigating away from login
            if (event.url !== "/login") {
                this._intendedUrl = "";
            }

            this.urlChanged.next({
                previousUrl: this._previousUrl,
                currentUrl: this._currentUrl,
                intendedUrl: this._intendedUrl,
            });
        });
    }

    // Method to set intended destination for protected routes
    setIntendedUrl(url: string) {
        this._intendedUrl = url;
    }

    // Route history methods
    getPreviousUrl(): string {
        return this._previousUrl;
    }

    getCurrentUrl(): string {
        return this._currentUrl;
    }

    // Redirect after login method
    public redirectingPrevioulyUrl() {
        let url = this._intendedUrl || this._previousUrl || "/home";

        const [path, queryString] = url.split("?");

        const queryParams: Record<string, string> = queryString
            ? queryString
                  .split("&")
                  .map((kv) => kv.split("="))
                  .reduce(
                      (acc, [k, v]) => {
                          acc[k] = v;
                          return acc;
                      },
                      {} as Record<string, string>,
                  )
            : {};
        const options: NavigationExtras = { queryParams };
        if (url === this._previousUrl) {
            options.state = { productData: this._previousProduct };
        }

        this.router.navigate([path], options);
        this._intendedUrl = "";
    }

    public redirectingCurrentUrl() {
        let url = this._intendedUrl || this._currentUrl || "/home";

        const [path, queryString] = url.split("?");

        const queryParams: Record<string, string> = queryString
            ? queryString
                  .split("&")
                  .map((kv) => kv.split("="))
                  .reduce(
                      (acc, [k, v]) => {
                          acc[k] = v;
                          return acc;
                      },
                      {} as Record<string, string>,
                  )
            : {};
        const options: NavigationExtras = { queryParams };
        if (url === this._previousUrl) {
            options.state = { productData: this._previousProduct };
        }
        if (path.startsWith("/designer")) {
            this.router.navigate([path], options);
            return;
        }
        this.router.navigate(["/"], { skipLocationChange: true }).then(() => {
            this.router.navigate([path], options);
        });
    }
}
